import { PlusOutlined } from '@ant-design/icons';
import { Badge, Button } from 'antd';
import { AdminContext, NewOrdersContext } from 'constants/contexts';
import { SIDEBAR_ITEMS } from 'constants/sidebar-items';
import { PLATFORM_SIDEBAR_ITEMS } from 'constants/platform-sidebar-items';
import useConfig from 'hooks/useConfig';
import { useHasAnyOfPermissionsCallback } from 'hooks/useHasPermission';
import React, { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';

function useSidebarItems(collapsed) {
  const hasAccess = useHasAnyOfPermissionsCallback();
  const navigate = useNavigate();
  const config = useConfig('general');
  const location = useLocation();
  const { user } = useContext(AdminContext);
  const isPlatform = location.pathname.startsWith('/platform');

  return useMemo(() => {
    const sourceItems = isPlatform ? PLATFORM_SIDEBAR_ITEMS : SIDEBAR_ITEMS;
    const topItemsConfig = sourceItems.filter((i) => !i.bottom).filter(
      (item) => !(item.label === 'Global menus' && !config?.globalMenusEnabled),
    );
    const bottomItemsConfig = sourceItems.filter((i) => i.bottom).filter(
      (item) => !item.superuserOnly || user?.is_superuser,
    );

    const topItems = generateSidebarItems(topItemsConfig);
    const bottomItems = generateSidebarItems(bottomItemsConfig);

    const itemsMap = {};
    [...topItemsConfig, ...bottomItemsConfig].forEach((item) => {
      if (item.route?.path) {
        itemsMap[item.route.path] = item;
      }
    });

    return { topItems, bottomItems, itemsMap, isPlatform };

    function generateSidebarItems(items) {
      function generateMenuItem(menu) {
        return {
          key: menu.subMenus ? null : menu.route.path,
          label: menu.action ? (
            <div className="flex items-center justify-between gap-2 w-full">
              <div className="flex-1 flex items-center justify-between w-full">
                {menu.label}
                <NavBadge route={menu.route.path} badge={menu.badge} />
              </div>
              {!collapsed && (
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      menu.action.route,
                      menu.action.state ? { state: menu.action.state } : undefined,
                    );
                  }}
                />
              )}
            </div>
          ) : (
            <div className="flex w-full">
              {menu.label}
            </div>
          ),
          icon: menu.icon,
          className: menu.action?.route && 'custom-menu-item',
          children: menu.subMenus ? menu.subMenus.map(generateMenuItem) : null,
        };
      }

      return items
        .filter((n) => isPlatform || !n.permissions || hasAccess(n.permissions))
        .map(generateMenuItem);
    }
  }, [hasAccess, collapsed, config, navigate, isPlatform, user?.is_superuser]);
}

function NavBadge({ badge, route }) {
  const { newOrderIds, newScheduledOrderIds } = useContext(NewOrdersContext);
  const newOrdersCount = new Set([...newOrderIds, ...newScheduledOrderIds]).size;
  const location = useLocation();
  const badges = {
    orders: () => {
      if (newOrdersCount === 0) {
        return null;
      }
      return newOrdersCount > 9 ? '+9' : newOrdersCount;
    },
  };
  if (!badge || !badges?.[badge]() || location.pathname.includes(route.path)) {
    return null;
  }
  return <Badge count={badges[badge]()} className="ml-4" />;
}

export default useSidebarItems;
